
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { storage } from '@shared/utils/storage';
import { auth, db } from '@shared/services/firebase';
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  increment,
  setDoc,
  getDoc,
  orderBy,
  limit,
  arrayUnion,
  runTransaction,
  getDocs,
  deleteDoc
} from "firebase/firestore";
import { Journey, Session, KnowledgeDoc, User, StrategicPlan, UserFeedback, JourneyAnswerSet } from '@shared/types';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import JourneyForm from './components/JourneyForm';
import JourneyDetail from './components/JourneyDetail';
import InterviewSession from './components/InterviewSession';
import Insights from './components/Insights';
import Landing from './components/Landing';
import PinnedList from './components/PinnedList';
import Auth from './components/Auth';
import Login from './components/Login';
import Register from './components/Register';
import FeedbackModal from './components/FeedbackModal';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import { generateStrategicPlan, generateBlueprintAnswers } from '@shared/services/gemini';
import { SYSTEM_TEXTBOOK_DOCS } from '@shared/utils/seed';
import { buildKnowledgeContext } from '@shared/utils/knowledge';

type View = 'landing' | 'dashboard' | 'new-journey' | 'journey-detail' | 'session' | 'session-review' | 'insights' | 'pinned' | 'auth' | 'login' | 'register' | 'blog' | 'blog-post';

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [view, setView] = useState<View>('landing');
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [knowledgeDocs, setKnowledgeDocs] = useState<KnowledgeDoc[]>([]);
  const [systemDocs, setSystemDocs] = useState<KnowledgeDoc[]>(SYSTEM_TEXTBOOK_DOCS);
  const [strategicPlan, setStrategicPlan] = useState<StrategicPlan | null>(null);
  const [activeJourney, setActiveJourney] = useState<Journey | null>(null);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [journeyAnswers, setJourneyAnswers] = useState<Record<string, JourneyAnswerSet[]>>({});
  const [loading, setLoading] = useState(true);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(true);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const processedInSession = useRef(new Set<string>());
  const unsubscribersRef = useRef<{ user?: () => void; payments?: () => void }>({});

  useEffect(() => {
    const checkKey = async () => {
      if ((window as any).aistudio) {
        const selected = await (window as any).aistudio.hasSelectedApiKey();
        setHasApiKey(selected);
      }
    };
    checkKey();

    // Handle Stripe payment success
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      // Clear the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
      // Show success message
      alert('Payment successful! Credits have been added to your account.');
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && firebaseUser.emailVerified) {
        // Önceki listener'ları temizle
        if (unsubscribersRef.current.user) {
          unsubscribersRef.current.user();
        }
        if (unsubscribersRef.current.payments) {
          unsubscribersRef.current.payments();
        }

        const userDocRef = doc(db, "users", firebaseUser.uid);

        try {
          const userSnap = await getDoc(userDocRef);
          if (!userSnap.exists()) {
            await setDoc(userDocRef, {
              email: firebaseUser.email,
              fullName: firebaseUser.displayName || 'Doctor',
              credits: 20,
              processedPayments: [],
              createdAt: new Date().toISOString()
            });
          }
        } catch (e) { console.warn("Auth sync error"); }

        const unsubUser = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setCurrentUser({
              id: firebaseUser.uid,
              email: data.email || firebaseUser.email || '',
              fullName: data.fullName || firebaseUser.displayName || 'Doctor',
              credits: data.credits ?? 0,
              isPremium: data.isPremium ?? false
            });
          }
        });

        const paymentsRef = collection(db, "customers", firebaseUser.uid, "payments");
        const qPayments = query(paymentsRef, where("status", "==", "succeeded"));

        const unsubPayments = onSnapshot(qPayments, async (snapshot) => {
          const userRef = doc(db, "users", firebaseUser.uid);

          // Her yeni payment için ayrı ayrı işle (race condition önleme)
          for (const change of snapshot.docChanges()) {
            const paymentId = change.doc.id;
            const paymentData = change.doc.data();

            if (change.type === "added" && paymentData?.status === "succeeded") {
              // Firestore transaction ile güvenli işlem
              await runTransaction(db, async (transaction) => {
                const userDoc = await transaction.get(userRef);
                if (!userDoc.exists()) return;

                const processed = userDoc.data()?.processedPayments || [];

                // Eğer bu payment henüz işlenmemişse işle
                if (!processed.includes(paymentId)) {
                  transaction.update(userRef, {
                    credits: increment(20), // Sabit 20 kredi
                    processedPayments: arrayUnion(paymentId)
                  });
                }
              });
            }
          }
        });

        // Yeni listener'ları ref'e kaydet
        unsubscribersRef.current.user = unsubUser;
        unsubscribersRef.current.payments = unsubPayments;

        loadUserData(firebaseUser.uid);
      } else {
        // Kullanıcı çıkış yaptığında listener'ları temizle
        if (unsubscribersRef.current.user) {
          unsubscribersRef.current.user();
          unsubscribersRef.current.user = undefined;
        }
        if (unsubscribersRef.current.payments) {
          unsubscribersRef.current.payments();
          unsubscribersRef.current.payments = undefined;
        }

        setCurrentUser(null);
        setJourneys([]);
        setSessions([]);
        setLoading(false);
        if (view !== 'landing' && view !== 'auth' && view !== 'blog' && view !== 'login' && view !== 'register') setView('landing');
      }
    });
    return () => {
      unsubscribe();
      // Tüm listener'ları temizle
      if (unsubscribersRef.current.user) {
        unsubscribersRef.current.user();
      }
      if (unsubscribersRef.current.payments) {
        unsubscribersRef.current.payments();
      }
    };
  }, []); // Sadece bir kez çalış

  useEffect(() => {
    let active = true;
    const loadBook = async () => {
      try {
        const res = await fetch('/medical_interviews.txt');
        if (!res.ok) return;
        const text = await res.text();
        if (!active || !text.trim()) return;
        setSystemDocs(prev => {
          if (prev.some(d => d.id === 'system-medical-interviews-book')) return prev;
          return [
            ...prev,
            {
              id: 'system-medical-interviews-book',
              userId: 'system',
              title: 'Medical Interviews - Comprehensive Guide (4th Ed)',
              isSystem: true,
              tags: ['Textbook', 'ISC Medical'],
              content: text
            }
          ];
        });
      } catch (e) {
        console.warn('Book load failed', e);
      }
    };
    loadBook();
    return () => { active = false; };
  }, []);

  // Handle view changes based on auth state
  useEffect(() => {
    if (currentUser && (view === 'landing' || view === 'auth' || view === 'login' || view === 'register')) {
      navigate('/portal');
      setView('dashboard');
    } else if (!currentUser && (view === 'dashboard' || view === 'new-journey' || view === 'journey-detail' || view === 'session' || view === 'session-review' || view === 'insights' || view === 'pinned')) {
      navigate('/');
      setView('landing');
    } else if (currentUser && (view === 'dashboard' || view === 'new-journey' || view === 'journey-detail' || view === 'session' || view === 'session-review' || view === 'insights' || view === 'pinned') && location.pathname !== '/portal') {
      // Ensure portal views stay on /portal URL
      window.history.replaceState(null, '', '/portal');
    }
  }, [currentUser, view, navigate, location.pathname]);

  // Handle URL routing for /blog, /login, /register, /portal and /blog/:slug
  useEffect(() => {
    if (location.pathname === '/blog') {
      setView('blog');
    } else if (location.pathname.startsWith('/blog/')) {
      setView('blog-post');
    } else if (location.pathname === '/login') {
      setView('login');
    } else if (location.pathname === '/register') {
      setView('register');
    } else if (location.pathname === '/portal') {
      // Stay on /portal for all portal views - don't change URL
      if (!currentUser) {
        navigate('/login');
      }
      // Don't set view here, let auth useEffect handle it
    } else if (location.pathname === '/' && (view === 'blog' || view === 'blog-post' || view === 'login' || view === 'register')) {
      // Allow navigation away from blog/auth to home
      setView('landing');
    }
  }, [location.pathname, currentUser, navigate]);

  const loadUserData = async (userId: string) => {
    setLoading(true);
    try {
      const [j, s, k, p] = await Promise.all([
        storage.getJourneys(userId),
        storage.getSessions(userId),
        storage.getKnowledge(userId),
        storage.getStrategicPlan(userId)
      ]);
      setJourneys(j || []);
      setSessions(s || []);
      setKnowledgeDocs(k || []);
      setStrategicPlan(p);
    } catch (err) { } finally { setLoading(false); }
  };

  const handleSelectKey = async () => {
    if ((window as any).aistudio) {
      await (window as any).aistudio.openSelectKey();
      setHasApiKey(true);
    }
  };

  const handleAddCredits = async () => {
    if (!currentUser) return;
    setIsRedirectingToStripe(true);
    try {
      // Check if customer exists in Firestore
      const customerDocRef = doc(db, "customers", currentUser.id);
      const customerSnap = await getDoc(customerDocRef);

      let customerData = customerSnap.data();
      let customerId = customerData?.stripeId;

      // If customer doesn't exist in Firestore or has no stripeId, create one
      if (!customerSnap.exists() || !customerId) {
        console.log('Creating new customer for user:', currentUser.id);

        // Create customer document - extension will handle Stripe customer creation
        await setDoc(customerDocRef, {
          email: currentUser.email,
          name: currentUser.fullName || 'Customer',
          metadata: {
            firebaseUID: currentUser.id
          },
          created: new Date().toISOString()
        }, { merge: true });

        // Wait a moment for extension to create Stripe customer
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Re-fetch customer data
        const updatedSnap = await getDoc(customerDocRef);
        customerData = updatedSnap.data();
        customerId = customerData?.stripeId;

        if (!customerId) {
          throw new Error('Failed to create Stripe customer. Please try again later.');
        }
      }

      console.log('Using customer ID:', customerId);

      const checkoutRef = collection(db, "customers", currentUser.id, "checkout_sessions");

      // Clear any existing checkout sessions to prevent conflicts
      const existingSessions = await getDocs(checkoutRef);
      const deletePromises = existingSessions.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Create checkout session
      const newSessionRef = await addDoc(checkoutRef, {
        customer: customerId, // Specify the customer ID explicitly
        line_items: [{
          price_data: {
            currency: 'gbp',
            product_data: {
              name: '20 Credits Package'
            },
            unit_amount: 100 // 100 pence = £1.00
          },
          quantity: 1
        }],
        mode: "payment",
        success_url: window.location.origin + "?payment=success",
        cancel_url: window.location.origin,
        created: new Date().toISOString()
      });

      // Wait for Stripe to process the session and provide URL
      const checkForUrl = () => {
        return new Promise<void>((resolve, reject) => {
          const unsub = onSnapshot(newSessionRef, (doc) => {
            const data = doc.data();
            if (data?.url) {
              unsub();
              window.location.assign(data.url);
              resolve();
            } else if (data?.error) {
              unsub();
              reject(new Error(data.error.message || 'Stripe session creation failed'));
            }
          });

          // Timeout after 30 seconds
          setTimeout(() => {
            unsub();
            reject(new Error('Timeout waiting for Stripe checkout URL'));
          }, 30000);
        });
      };

      await checkForUrl();

    } catch (err: any) {
      setIsRedirectingToStripe(false);
      console.error('Stripe checkout error:', err);
      alert("Payment setup failed. Please try again. Error: " + (err.message || 'Unknown error'));
    }
  };

  const handleSubmitFeedback = async (category: any, content: string) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;
    try {
      await addDoc(collection(db, "user_feedbacks"), {
        userId: firebaseUser.uid,
        userName: firebaseUser.displayName || 'Doctor',
        userEmail: firebaseUser.email || '',
        category,
        content,
        createdAt: new Date().toISOString()
      });
    } catch (err) {
      console.error("Feedback error:", err);
      throw err;
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
    setView('landing');
  };

  const loadJourneyAnswers = async (journeyId: string) => {
    if (!currentUser) return;
    const answerSets = await storage.getJourneyAnswerSets(currentUser.id, journeyId);
    setJourneyAnswers(prev => ({ ...prev, [journeyId]: answerSets }));
  };

  const handleCreateJourney = async (journey: Journey) => {
    if (!currentUser) return;
    const count = journey.questions.length;
    if ((currentUser.credits || 0) < count) {
      alert("Insufficient credits!");
      return;
    }
    const userDocRef = doc(db, "users", currentUser.id);
    await updateDoc(userDocRef, { credits: increment(-count) });
    const created = await storage.createJourney({ ...journey, userId: currentUser.id });
    setJourneys(prev => [created, ...prev]);
    setActiveJourney(created);
    const createdSession = await storage.createSession({
      id: '', userId: currentUser.id, journeyId: created.id,
      startTime: new Date().toISOString(), attempts: [],
      isComplete: false, currentQuestionIdx: 0, activeFollowUp: null,
      isPublic: false
    });
    setSessions(prev => [createdSession, ...prev]);
    setActiveSession(createdSession);
    setView('session');
  };

  const handleStartSession = async (journey: Journey) => {
    if (!currentUser) return;
    const createdSession = await storage.createSession({
      id: '', userId: currentUser.id, journeyId: journey.id,
      startTime: new Date().toISOString(), attempts: [],
      isComplete: false, currentQuestionIdx: 0, activeFollowUp: null,
      isPublic: false
    });
    setSessions(prev => [createdSession, ...prev]);
    setActiveSession(createdSession);
    setActiveJourney(journey);
    setView('session');
  };

  const handleGenerateJourneyAnswers = async (journey: Journey) => {
    if (!currentUser) return;
    const cost = journey.questions.length;
    if ((currentUser.credits || 0) < cost) {
      throw new Error("INSUFFICIENT_CREDITS");
    }
    const knowledgeContext = buildKnowledgeContext(systemDocs, {
      query: `${journey.grade} ${journey.specialty} ${journey.interviewType}`,
      maxChars: 8000
    });
    const answers = await generateBlueprintAnswers(journey, knowledgeContext);
    if (!Object.keys(answers).length) {
      throw new Error("EMPTY_ANSWERS");
    }
    const userDocRef = doc(db, "users", currentUser.id);
    await updateDoc(userDocRef, { credits: increment(-cost) });
    await storage.saveJourneyAnswers(currentUser.id, journey.id, answers);
    const newSet: JourneyAnswerSet = {
      userId: currentUser.id,
      journeyId: journey.id,
      answers,
      createdAt: new Date().toISOString()
    };
    setJourneyAnswers(prev => ({ ...prev, [journey.id]: [newSet, ...(prev[journey.id] || [])] }));
  };

  const generatePlanFromSessions = async (sessionsToUse: Session[]) => {
    if (!currentUser) return;
    if (sessionsToUse.length === 0) return;
    setUpdatingPlan(true);
    try {
      const adviceItems = await generateStrategicPlan(sessionsToUse);
      const newPlan = { userId: currentUser.id, updatedAt: new Date().toISOString(), items: adviceItems };
      await storage.saveStrategicPlan(newPlan);
      setStrategicPlan(newPlan);
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleSaveSession = async (session: Session) => {
    if (!currentUser) return;
    await storage.updateSession(session);
    setSessions(prev => prev.map(s => s.id === session.id ? session : s));
    if (session.isComplete) {
      const allCompleted = sessions.filter(s => s.isComplete || s.id === session.id);
      await generatePlanFromSessions(allCompleted);
    }
  };

  const handleGeneratePlan = async () => {
    const completed = sessions.filter(s => s.isComplete);
    await generatePlanFromSessions(completed);
  };

  const handleAddKnowledge = async (docData: KnowledgeDoc, file?: File) => {
    const created = await storage.addKnowledgeDoc(docData, file);
    if (created) {
      setKnowledgeDocs(prev => [created, ...prev]);
    }
  };

  const handleDeleteKnowledge = async (docId: string, fileUrl?: string) => {
    await storage.deleteKnowledgeDoc(docId, fileUrl);
    setKnowledgeDocs(prev => prev.filter(d => d.id !== docId));
  };

  const handleReviewSession = (session: Session) => {
    const journey = journeys.find(j => j.id === session.journeyId);
    if (!journey) return;
    setActiveSession(session);
    setActiveJourney(journey);
    setView(session.isComplete ? 'session-review' : 'session');
  };

  const togglePin = async (questionId: string) => {
    const journey = journeys.find(j => j.questions.some(q => q.id === questionId));
    if (!journey) return;
    const updatedQuestions = journey.questions.map(q => q.id === questionId ? { ...q, isPinned: !q.isPinned } : q);
    const updatedJourney = { ...journey, questions: updatedQuestions };
    setJourneys(prev => prev.map(j => j.id === journey.id ? updatedJourney : j));
    if (activeJourney?.id === journey.id) setActiveJourney(updatedJourney);
    await storage.updateJourneyQuestions(journey.id, updatedQuestions);
  };

  if (loading || isRedirectingToStripe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 font-bold text-slate-400 uppercase tracking-widest text-[10px]">
          {isRedirectingToStripe ? 'Redirecting to Secure Payment...' : 'Synchronizing Doctor Dashboard...'}
        </p>
      </div>
    );
  }

  if (!hasApiKey && view !== 'landing' && view !== 'blog') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-8 text-center">
        <h2 className="text-3xl font-black text-slate-900 mb-4">API Key Required</h2>
        <p className="text-slate-500 mb-8">Please select a valid Gemini API key to proceed.</p>
        <button onClick={handleSelectKey} className="bg-slate-900 text-white px-10 py-5 rounded-2xl font-black">Select Key</button>
      </div>
    );
  }

  // Handle blog post routing separately
  if (location.pathname.startsWith('/blog/')) {
    return <BlogPost />;
  }

  const renderView = () => {
    const combinedKnowledgeDocs = [...systemDocs, ...knowledgeDocs];
    switch (view) {
      case 'landing': return <Landing onStart={() => navigate('/login')} onBlog={() => navigate('/blog')} />;
      case 'auth': return <Auth onLogin={() => setView('dashboard')} />;
      case 'login': return <Login onLogin={() => setView('dashboard')} onSwitchToRegister={() => navigate('/register')} />;
      case 'register': return <Register onLogin={() => setView('dashboard')} onSwitchToLogin={() => navigate('/login')} />;
      case 'blog': return <Blog />;
      case 'blog-post': return null; // Blog posts are now handled by routing
      case 'dashboard': return <Dashboard journeys={journeys} sessions={sessions} knowledgeDocs={combinedKnowledgeDocs} onNew={() => setView('new-journey')} onSelect={(j) => { setActiveJourney(j); loadJourneyAnswers(j.id); setView('journey-detail'); }} onSelectSession={handleReviewSession} />;
      case 'new-journey': return <JourneyForm onSubmit={handleCreateJourney} onCancel={() => setView('dashboard')} knowledgeDocs={combinedKnowledgeDocs} userCredits={currentUser?.credits ?? 0} journeys={journeys} />;
      case 'journey-detail': return activeJourney ? (
        <JourneyDetail
          journey={activeJourney}
          onBack={() => setView('dashboard')}
          userCredits={currentUser?.credits ?? 0}
          answerSets={journeyAnswers[activeJourney.id] || []}
          onGenerateAnswers={() => handleGenerateJourneyAnswers(activeJourney)}
        />
      ) : null;
      case 'session': return activeJourney && activeSession ? <InterviewSession journey={activeJourney} session={activeSession} knowledgeDocs={combinedKnowledgeDocs} onTogglePin={togglePin} onComplete={(finalSession) => { handleSaveSession(finalSession); setView('dashboard'); }} onExit={() => setView('dashboard')} /> : null;
      case 'session-review': return activeJourney && activeSession ? <InterviewSession journey={activeJourney} session={activeSession} knowledgeDocs={combinedKnowledgeDocs} onTogglePin={togglePin} onComplete={() => setView('dashboard')} onExit={() => setView('dashboard')} reviewMode={true} /> : null;
      case 'insights': return <Insights sessions={sessions} journeys={journeys} strategicPlan={strategicPlan} updatingPlan={updatingPlan} onGeneratePlan={handleGeneratePlan} onBack={() => setView('dashboard')} />;
      case 'pinned': return <PinnedList journeys={journeys} sessions={sessions} onBack={() => setView('dashboard')} />;
      default: return <Landing onStart={() => setView('auth')} onBlog={() => setView('blog')} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {view !== 'landing' && view !== 'auth' && view !== 'login' && view !== 'register' && view !== 'blog' && view !== 'blog-post' && currentUser && (
        <Navigation
          currentView={view}
          setView={setView}
          onLogout={handleLogout}
          userName={currentUser.fullName}
          credits={currentUser.credits ?? 0}
          onAddCredits={handleAddCredits}
          onOpenFeedback={() => setIsFeedbackOpen(true)}
          onNavigateToBlog={() => navigate('/blog')}
        />
      )}
      <main className={`flex-grow container mx-auto px-0 sm:px-4 ${view === 'landing' || view === 'auth' || view === 'login' || view === 'register' || view === 'blog' || view === 'blog-post' ? 'py-0' : 'py-4 sm:py-8'}`}>
        <div className={`${view === 'landing' || view === 'auth' || view === 'login' || view === 'register' || view === 'blog' || view === 'blog-post' ? '' : 'bg-white rounded-none sm:rounded-[3rem] shadow-none sm:shadow-2xl border-none sm:border border-slate-100 min-h-screen sm:min-h-[700px] overflow-hidden'}`}>
          {renderView()}
        </div>
      </main>
      <footer className="bg-white/50 border-t border-slate-100 py-10 mt-12 text-center">
        <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em]">NHSInterview • Enterprise Ver. 6.3.1</p>
      </footer>
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        onSubmit={handleSubmitFeedback}
      />
      <Analytics />
    </div>
  );
};

export default App;
